import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Modal, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { getDataList, crearLotes } from '../../../api/common';

/**
 * EquipoExcel - Component for Excel upload of equipment
 * Formato compatible: MIS-GCO-FOR-004 (BIOLAB)
 * Header en fila 28, datos desde fila 30 (fila 29 = ejemplo "Ej:")
 *
 * Columnas esperadas (nombres exactos del Excel):
 *   #, Item, Marca, Modelo , Capacidad o Rango de trabajo,
 *   Serie , Inventario , Ubicación , Resolución , Puntos a Calibrar , Observación
 */
const EquipoExcel = ({ clienteId, sedeId, onValidarDuplicados, onResolverItems, onAgregar, onEquiposCreados }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [fileInfo, setFileInfo] = useState(null);
    const [modalConfirmacion, setModalConfirmacion] = useState({ show: false, equipos: [] });

    useEffect(() => {
        loadItems();
    }, []);

    const loadItems = async () => {
        setLoading(true);
        try {
            const response = await getDataList('Items');
            setItems(response.data.data || []);
        } catch (err) {
            setError('Error al cargar items: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setProcessing(true);
        setError(null);
        setFileInfo(null);

        try {
            const data = await readFileAsync(file);

            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];

            // ─── CAMBIO CLAVE 1 ────────────────────────────────────────────────
            // range: 27 → usa la fila 28 del Excel (0-indexed) como encabezado.
            // Así XLSX lee exactamente: '#', 'Item', 'Marca', 'Modelo ', 'Serie ', etc.
            const rawRows = XLSX.utils.sheet_to_json(sheet, { range: 27 });

            // ─── CAMBIO CLAVE 2 ────────────────────────────────────────────────
            // Filtrar la fila de ejemplo ('Ej:') y las filas vacías (solo tienen '#')
            const rows = rawRows.filter(row => {
                const num = row['#'];
                const itemVal = (row['Item'] || '').toString().trim();
                return num !== 'Ej:' && itemVal !== '';
            });

            if (rows.length === 0) {
                setError('El archivo no contiene equipos con datos válidos');
                setProcessing(false);
                return;
            }

            const { resueltos, sinResolver } = processRows(rows);

            setFileInfo({
                total: rows.length,
                resueltos: resueltos.length,
                sinResolver: sinResolver.length,
            });

            if (sinResolver.length > 0) {
                onResolverItems(sinResolver, (equiposResueltos) => {
                    const todosEquipos = [...resueltos, ...equiposResueltos];
                    setModalConfirmacion({ show: true, equipos: todosEquipos });
                });
                setProcessing(false);
            } else {
                setModalConfirmacion({ show: true, equipos: resueltos });
                setProcessing(false);
            }
        } catch (err) {
            setError('Error al procesar archivo: ' + err.message);
            setProcessing(false);
        }

        e.target.value = '';
    };

    const readFileAsync = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(new Uint8Array(e.target.result));
            reader.onerror = () => reject(new Error('Error al leer archivo'));
            reader.readAsArrayBuffer(file);
        });
    };

    const processRows = (rows) => {
        const resueltos = [];
        const sinResolver = [];

        rows.forEach((row) => {
            // ─── CAMBIO CLAVE 3 ────────────────────────────────────────────────
            // Nombres de columna exactos del formato MIS-GCO-FOR-004.
            // Varios tienen espacio al final ('Serie ', 'Modelo ', etc.).
            // .toString().trim() cubre series numéricas (ej: 1235 → '1235').
            const itemNombre   = (row['Item']                        || '').toString().trim();
            const serie        = (row['Serie ']                      || '').toString().trim();
            const capacidad    = (row['Capacidad o Rango de trabajo']|| '').toString().trim();
            const observacion  = (row['Observación ']                || '').toString().trim();
            
            // Valores con default "No Presenta"
            const marcaRaw     = (row['Marca']                       || '').toString().trim();
            const modeloRaw    = (row['Modelo ']                     || '').toString().trim();
            const inventarioRaw= (row['Inventario ']                 || '').toString().trim();
            
            // Valores con default "No Informa"
            const ubicacionRaw = (row['Ubicación ']                  || '').toString().trim();
            const resolucionRaw= (row['Resolución ']                 || '').toString().trim();
            const puntosRaw    = (row['Puntos a Calibrar ']          || '').toString().trim();
            
            // Asignar valores por defecto
            const marca        = marcaRaw     || 'No Presenta';
            const modelo       = modeloRaw    || 'No Presenta';
            const inventario   = inventarioRaw|| 'No Presenta';
            const ubicacion    = ubicacionRaw || 'No Informa';
            const resolucion   = resolucionRaw|| 'No Informa';
            const puntos       = puntosRaw    || 'No Informa';

            // Item es obligatorio; serie también (es la clave de unicidad)
            if (!itemNombre ) return;

            // Buscar coincidencia en el catálogo de items (igual que antes)
            const itemMatch = items.find(i =>
                i.nombre.toLowerCase().includes(itemNombre.toLowerCase()) ||
                itemNombre.toLowerCase().includes(i.nombre.toLowerCase())
            );

            const equipo = {
                item: itemMatch?._id || null,
                itemNombreBuscado: itemNombre,
                serie,
                marca,
                modelo,
                capacidad,      // campo extra del formato
                inventario,
                ubicacion,
                resolucion,
                puntos,
                observacion,
                cliente: clienteId,
                sedeClient: sedeId,
            };

            if (itemMatch) {
                resueltos.push(equipo);
            } else {
                sinResolver.push(equipo);
            }
        });

        return { resueltos, sinResolver };
    };

    const handleConfirmEquipos = async () => {
        const equipos = modalConfirmacion.equipos;
        console.log('✅ handleConfirmEquipos: Confirmando', equipos.length, 'equipos');
        setModalConfirmacion({ show: false, equipos: [] });
        await validateAndAdd(equipos);
    };

    const handleCancelConfirmacion = () => {
        setModalConfirmacion({ show: false, equipos: [] });
        setFileInfo(null);
    };

    const validateAndAdd = async (equipos) => {
        console.log('🔍 validateAndAdd iniciado con', equipos.length, 'equipos');
        setProcessing(true);
        try {
            const seriesParaValidar = equipos
                .map(e => (e.serie || '').toString().trim())
                .filter(s => s !== '');
            console.log('🔍 Series a validar:', seriesParaValidar);

            // Default result treats all equipos as new (used when no series to validate)
            let result = { cancelled: false, nuevosData: equipos.map(e => (e.serie || '').toString().trim()), duplicadosData: [] };

            if (seriesParaValidar.length > 0) {
                result = await onValidarDuplicados(seriesParaValidar);
                console.log('🔍 Resultado de validación:', result);

                if (result.cancelled) {
                    console.log('❌ Validación cancelada por usuario');
                    setProcessing(false);
                    return;
                }
            }

            // Collect all equipos to add
            const equiposToAdd = [];

            // Add existing duplicates
            if (result.duplicadosData.length > 0) {
                console.log('📦 Agregando', result.duplicadosData.length, 'duplicados existentes');
                equiposToAdd.push(...result.duplicadosData);
            }

            // Create new equipos
            const equiposToCreate = equipos.filter(eq =>
                !(eq.serie || '').trim() || result.nuevosData.includes(eq.serie)
            );
            console.log('🆕 Equipos a crear:', equiposToCreate.length);

            let expansionInfo = null;
            if (equiposToCreate.length > 0) {
                const response = await crearLotes('Equipos', equiposToCreate);
                // Nuevo shape (spec items-adicionales-equipo-dependiente): { equipos, expansion: { padres, hijos, total } }
                // Retro-compat: si el backend antiguo no devuelve `expansion`, se calcula fallback.
                const createdEquipos = response.data.equipos || [];
                expansionInfo = response.data.expansion || null;
                equiposToAdd.push(...createdEquipos);

                // Notify parent to refresh EquipoExistente list
                if (onEquiposCreados) {
                    onEquiposCreados();
                }
            }

            console.log('📊 Total equiposToAdd:', equiposToAdd.length, 'equipos');
            // Add all equipos at once
            if (equiposToAdd.length > 0) {
                onAgregar(equiposToAdd);
            }

            const msgExpansion = expansionInfo && expansionInfo.hijos > 0
                ? ` (${expansionInfo.padres} padres + ${expansionInfo.hijos} dependientes automáticos)`
                : '';
            setSuccess(`${equiposToAdd.length} equipo(s) agregado(s) correctamente${msgExpansion}`);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Error al validar equipos: ' + (err.response?.data?.message || err.message));
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div>
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
                    {success}
                </Alert>
            )}
            {fileInfo && (
                <Alert variant="info">
                    <strong>Archivo procesado:</strong> {fileInfo.total} equipo(s) encontrado(s)
                    <br />
                    {fileInfo.resueltos} resuelto(s) automáticamente
                    {fileInfo.sinResolver > 0 && `, ${fileInfo.sinResolver} requieren selección manual de item`}
                </Alert>
            )}

            <Form.Group className="mb-3">
                <Form.Label>Cargar Archivo Excel</Form.Label>
                <Form.Control
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    disabled={loading || processing || !clienteId || !sedeId}
                />
                <Form.Text className="text-muted">
                    Formato aceptado: <strong>MIS-GCO-FOR-004</strong> (BIOLAB).
                    Columnas requeridas: <code>Item</code>,.
                    Opcionales:Serie Marca, Modelo, Capacidad, Inventario, Ubicación, Resolución, Puntos a Calibrar, Observación.
                </Form.Text>
            </Form.Group>

            {(loading || processing) && (
                <div className="text-center">
                    <Spinner animation="border" />
                    <p className="mt-2">
                        {loading ? 'Cargando items...' : 'Procesando archivo...'}
                    </p>
                </div>
            )}

            {(!clienteId || !sedeId) && (
                <Alert variant="warning">
                    Debe seleccionar un cliente y una sede antes de cargar equipos.
                </Alert>
            )}

            {/* Modal de confirmación con vista previa de equipos */}
            <Modal show={modalConfirmacion.show} onHide={handleCancelConfirmacion} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Equipos del Excel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-3">
                        <strong>Total de equipos:</strong> {modalConfirmacion.equipos.length}
                    </p>
                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        <Table striped bordered hover size="sm">
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: '50px' }}>#</th>
                                    <th>Item</th>
                                    <th>Marca</th>
                                    <th>Modelo</th>
                                    <th>Serie</th>
                                    <th>Inventario</th>
                                    <th>Ubicación</th>
                                    <th>Resolución</th>
                                    <th>Puntos</th>
                                    <th>Observación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {modalConfirmacion.equipos.map((equipo, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{equipo.itemNombreBuscado || 'N/A'}</td>
                                        <td>{equipo.marca || '-'}</td>
                                        <td>{equipo.modelo || '-'}</td>
                                        <td>{equipo.serie}</td>
                                        <td>{equipo.inventario || '-'}</td>
                                        <td>{equipo.ubicacion || '-'}</td>
                                        <td>{equipo.resolucion || '-'}</td>
                                        <td>{equipo.puntos || '-'}</td>
                                        <td>{equipo.observacion || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelConfirmacion}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleConfirmEquipos}>
                        Confirmar y Agregar ({modalConfirmacion.equipos.length} equipos)
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EquipoExcel;